import { ContainerModel } from '#lib/ContainerModel.svelte.js';
import ImageEntryModel from '#lib/ImageEntryModel.svelte.js';

import {
  extract_text_teaser_from_container,
  extract_images_from_container,
  format_date_range,
  format_price,
  nanoid,
} from '#lib/util.js';
import { LANG } from '#lib/i18n.svelte.js';

const VALID_EVENT_STATUSES = new Set(['scheduled', 'cancelled', 'postponed']);

export default class EventEntryModel {
  // system-native props
  entry_id = $state();
  type = $state();
  slug = $state();
  created_at = $state();
  updated_at = $state();
  user_id = $state();
  visibility = $state('public');
  preview_title = $state('');
  preview_preamble = $state('');
  preview_description = $state('');
  preview_images_multiref = $state([]);
  preview_images = $state([]);
  
  // Entry tags
  tag_keys = $state([]); // Array of tag keys assigned to this entry
  exclude_from_search = $state(false); // hidden from site search, sitemap and search engines

  // type-specific custom props
  title = $state('');
  starts_at = $state(undefined);
  ends_at = $state(undefined);
  all_day = $state(false);
  subtitle = $state('');
  event_status = $state('scheduled'); // 'scheduled' | 'cancelled' | 'postponed'

  // Event registration settings
  registration_mode = $state('internal'); // 'internal' | 'external' | 'disabled'
  max_attendees = $state(); // How many people can attend the event; null = unlimited
  max_guests_per_registration = $state(5); // How many guests can a member register (not applicable to admins)
  waitlist_capacity = $state(0); // How many people can be on the waitlist (not applicable to admins)
  display_capacity = $state(true); // Whether to display the capacity on the event page to non-admins
  registration_notification_email = $state(''); // Email address to notify when users register
  link_href = $state(''); // External registration link URL
  link_button_label = $state(''); // Custom button label for registration CTA

  // Paid registrations (Stripe Checkout); paid events have no waitlist.
  // The price is the switch: any positive price makes the event paid.
  price_amount = $state(undefined); // Price per attendee in the currency's smallest unit (e.g. cents)
  price_currency = $state('EUR'); // ISO 4217 code
  // The normalized code every price display uses: a full three-letter code,
  // or EUR while the field is empty or half-typed ('U', 'US')
  currency_code = $derived(/^[A-Za-z]{3}$/.test(this.price_currency || '') ? this.price_currency.toUpperCase() : 'EUR');
  payment_required = $derived(Number.isInteger(this.price_amount) && this.price_amount > 0);
  // A half-typed currency code ('U', 'US') would make Intl throw; this derived
  // is read live while editing (preview, SEO), so it must never crash the editor
  formatted_price = $derived.by(() => {
    if (!this.payment_required) return '';
    const code = this.currency_code;
    try {
      return format_price(this.price_amount, code, LANG);
    } catch {
      return `${(this.price_amount / 100).toFixed(2)} ${code}`;
    }
  });

  // New image handling (multiple images)
  images_multiref = $state([]); 
  images = $state([]);

  body = $state(undefined);

  // Series/recurrence props
  series_id = $state(undefined);       // shared across all events in a series
  series_pattern = $state(undefined);  // { frequency, interval, end_date }
  series_index = $state(undefined);    // 0 = base event, 1+ = generated occurrences

  preview = $derived(this.to_preview());

  constructor({
    entry_id,
    type,
    slug,
    created_at,
    updated_at,
    user_id,
    visibility,
    preview_title,
    preview_preamble,
    preview_description,
    preview_images_multiref,
    preview_images, // computed from preview_images_multiref
    tag_keys = [],
    exclude_from_search,
    title,
    starts_at,
    ends_at,
    all_day,
    subtitle = '',
    event_status = 'scheduled',
    max_attendees = 30,
    max_guests_per_registration = 5,
    waitlist_capacity = 0,
    display_capacity = true,
    registration_mode = 'internal',
    registration_notification_email = '',
    link_href = '',
    link_button_label = '',
    payment_required = false,
    price_amount,
    price_currency = 'EUR',
    images_multiref = [],
    images = [],
    body = [],
    // Series/recurrence fields
    series_id,
    series_pattern,
    series_index,
    // Legacy fields - kept for migration only
    image_ref,
    image,
    // link_title,
    // link_description,
    allow_registrations, // Legacy field
  } = {}) {
    // system-native props
    this.entry_id = entry_id,
    this.type = type;
    this.slug = slug;
    this.created_at = created_at;
    this.updated_at = updated_at;
    this.user_id = user_id;
    this.visibility = visibility ?? 'public';
    this.preview_title = preview_title ?? '';
    this.preview_preamble = preview_preamble ?? '';
    this.preview_description = preview_description ?? '';
    this.preview_images_multiref = preview_images_multiref ?? [];
    this.preview_images = preview_images?.map(i => new ImageEntryModel(i)) ?? [];
    this.tag_keys = tag_keys ?? [];
    this.exclude_from_search = exclude_from_search ?? false;

    // type-specific custom props
    this.title = title ?? ''
    this.starts_at = starts_at ?? ''
    this.ends_at = ends_at ?? ''
    this.all_day = all_day ?? undefined
    this.subtitle = subtitle;
    this.event_status = VALID_EVENT_STATUSES.has(event_status) ? event_status : 'scheduled';
    this.max_attendees = max_attendees;
    this.max_guests_per_registration = max_guests_per_registration;
    this.waitlist_capacity = waitlist_capacity;
    this.display_capacity = display_capacity;
    this.registration_mode = registration_mode;
    this.registration_notification_email = registration_notification_email;
    this.link_href = link_href;
    this.link_button_label = link_button_label;
    // Entries saved before the price became the switch may carry a price with
    // payment_required unchecked: those were free events and stay free
    this.price_amount = payment_required === true && Number.isInteger(price_amount) ? price_amount : undefined;
    this.price_currency = price_currency || 'EUR';
    // Handle legacy allow_registrations field
    if (allow_registrations === false) {
      this.registration_mode = 'disabled';
    }
    // Series/recurrence
    this.series_id = series_id;
    this.series_pattern = series_pattern;
    this.series_index = series_index;

    this.images_multiref = images_multiref ?? [];
    this.images = images?.map(i => new ImageEntryModel(i)) ?? [];

    // Handle legacy single image migration
    if (image_ref && !images_multiref.includes(image_ref)) {
      this.images_multiref = [image_ref, ...images_multiref];
    }
    if (image && !images.some(img => img.entry_id === image.entry_id)) {
      this.images = [new ImageEntryModel(image), ...this.images];
    }

    // First create the blocks array
    let blocks = [...(body || [])];

    // Then create the ContainerModel with all blocks (an empty body gets a placeholder block with its own id)
    this.body = new ContainerModel({
      blocks: blocks.length ? blocks : [{ block_id: nanoid(), type: 'story', description: '' }],
      block_types: ['story'],
      default_block_type: 'story'
    }, this);
  }

  to_preview() {
    return {
      // system-native props
      entry_id: this.entry_id,
      slug: this.slug,
      type: this.type,
      preamble: this.preview_preamble || [
        format_date_range(this.starts_at, this.ends_at, this.all_day),
        this.registration_mode === 'internal' ? this.formatted_price : ''
      ].filter(Boolean).join(', '),
      title: this.preview_title || this.title,
      description: this.preview_description || extract_text_teaser_from_container(this.body),
      // Use to_json() for consistent serialization including variants
      images: this.preview_images?.length > 0
        ? this.preview_images.map(i => i.to_json())
        : (this.images.length > 0 
          ? this.images.map(i => i.to_json())
          : extract_images_from_container(this.body).slice(0, 3)),
      created_at: this.created_at,
      updated_at: this.updated_at,
      visibility: this.visibility,
      tag_keys: this.tag_keys,
      // custom preview props - still required e.g. for calendar view
      starts_at: this.starts_at,
      ends_at: this.ends_at,
      all_day: this.all_day,
      subtitle: this.subtitle,
      event_status: this.event_status,
      max_attendees: this.max_attendees,
      registration_mode: this.registration_mode,
      link_href: this.link_href,
      link_button_label: this.link_button_label,
      payment_required: this.payment_required,
      price_amount: this.price_amount,
      price_currency: this.price_currency,
      // Series
      ...(this.series_id && { series_id: this.series_id }),
    };
  }

  /** Dispose all child effects (delegates to body container) */
  dispose() {
    this.body?.dispose?.();
  }

  to_json() {
    return {
      entry_id: this.entry_id,
      type: this.type,  
      visibility: this.visibility,
      preview_title: this.preview_title,
      preview_preamble: this.preview_preamble,
      preview_description: this.preview_description,
      preview_images_multiref: this.preview_images_multiref,
      title: this.title,
      starts_at: this.starts_at,
      ends_at: this.ends_at,
      all_day: this.all_day,
      subtitle: this.subtitle,
      event_status: this.event_status,
      max_attendees: this.max_attendees ?? null, // null = unlimited
      max_guests_per_registration: this.max_guests_per_registration ?? 1,
      waitlist_capacity: this.waitlist_capacity ?? 0,
      display_capacity: this.display_capacity,
      registration_mode: this.registration_mode,
      registration_notification_email: this.registration_notification_email,
      link_href: this.link_href,
      link_button_label: this.link_button_label,
      payment_required: this.payment_required,
      price_amount: this.payment_required ? this.price_amount : undefined,
      // Never persist an empty code: it would read as unconfigured on the server
      price_currency: this.price_currency || 'EUR',
      images_multiref: $state.snapshot(this.images_multiref),
      body: this.body.to_json(),
      tag_keys: this.tag_keys,
      exclude_from_search: this.exclude_from_search || undefined,
      // Series (only include when set)
      ...(this.series_id && { series_id: this.series_id }),
      ...(this.series_pattern && { series_pattern: this.series_pattern }),
      ...(this.series_index !== undefined && { series_index: this.series_index }),
    };
  }

  toJSON() {
    return this.to_json();
  }
}

